/**
 * LIST MOTION — rows that arrive in order, leave when removed, and slide
 * into their new place when the list reorders.
 *
 *   <StaggerList>
 *     <Presence>
 *       {rows.map((r, i) => <StaggerItem key={r.id} index={i}>…</StaggerItem>)}
 *     </Presence>
 *   </StaggerList>
 *
 * `index` caps the cascade at STAGGER.maxItems: rows past that arrive at
 * once. `layout` on each item is what makes a sort or a removal animate the
 * survivors' positions — and it is what makes this unsuitable for hundreds
 * of rows at once, so keep it to paged or search-narrowed lists.
 *
 * For a plain element that should just carry the row variants, spread
 * `listItemMotion(i)` onto any motion element (`m.tr`, `m.li`).
 */
import React from 'react';
import { motion } from 'framer-motion';
import { listContainer, listItem } from './presets';

export const listItemMotion = (index: number) => ({
  custom: index,
  variants: listItem,
  initial: 'hidden' as const,
  animate: 'visible' as const,
  exit: 'exit' as const,
  layout: true as const,
});

interface StaggerListProps {
  children: React.ReactNode;
  className?: string;
  as?: 'div' | 'ul' | 'ol' | 'tbody';
  /** Replay the cascade on mount. Default on — a list arriving IS the moment. */
  initial?: boolean;
}

export const StaggerList: React.FC<StaggerListProps> = ({
  children,
  className,
  as = 'div',
  initial = true,
}) => {
  const Tag = motion[as];

  return (
    <Tag
      className={className}
      variants={listContainer}
      initial={initial ? 'hidden' : false}
      animate="visible"
    >
      {children}
    </Tag>
  );
};

interface StaggerItemProps {
  index: number;
  children: React.ReactNode;
  className?: string;
  as?: 'div' | 'li' | 'tr';
  /** Animate position changes (sort, removal of a sibling). Default on. */
  layout?: boolean;
}

export const StaggerItem: React.FC<StaggerItemProps> = ({
  index,
  children,
  className,
  as = 'div',
  layout = true,
}) => {
  const Tag = motion[as];

  return (
    <Tag
      className={className}
      custom={index}
      variants={listItem}
      initial="hidden"
      animate="visible"
      exit="exit"
      layout={layout}
    >
      {children}
    </Tag>
  );
};
